<template>
  <div>
    <loading
      v-show="isDownloading"
      :active="true" 
      :is-full-page="true"
      color="#498ceb"
    />

    <b-row>
      <b-col md="6">
        <donut-chart-state
          v-if="statesData.labels && statesData.labels.length"
          :data="statesData"
          i18nTitleVariable="casesByState"
          @filterByState="filterByState"
        />
      </b-col>
      <b-col md="6">
        <donut-chart-stage
          v-if="stagesData.labels && stagesData.labels.length"
          :data="stagesData"
          i18nTitleVariable="casesByStage"
          i18nStagesVariable="caseStages"
          @filterByStage="filterByStage"
        />
      </b-col>
    </b-row>
    <b-row>
      <b-col md="12">
        <bar-chart-worker
          v-if="workersData.labels && workersData.labels.length"
          :data="workersData"
        />
      </b-col>
    </b-row>
    <b-row>
      <b-col sm="3">
        <statistic-card-vertical :statistic="totalCases || 0" variant="info" iconName="ListIcon" i18nVariable="message.total" :isButton="true" :isSelected="activeState === 'total'" @filter="filterByState('total')"></statistic-card-vertical>
      </b-col>
      <b-col sm="3">
        <statistic-card-vertical :statistic="caseStats.completed || 0" variant="success" iconName="CheckIcon" i18nVariable="completed_all_m" :isButton="true" :isSelected="activeState === 'completed'" @filter="filterByState('completed')"></statistic-card-vertical>
      </b-col>
      <b-col sm="3">
        <statistic-card-vertical :statistic="caseStats.upToDate || 0" variant="primary" iconName="XIcon" i18nVariable="upToDate" :isButton="true" :isSelected="activeState === 'upToDate'" @filter="filterByState('upToDate')"></statistic-card-vertical>
      </b-col>
      <b-col sm="3">
        <statistic-card-vertical :statistic="caseStats.delayed || 0" variant="danger" iconName="ClockIcon" i18nVariable="delayed_all_m" :isButton="true" :isSelected="activeState === 'delayed'" @filter="filterByState('delayed')"></statistic-card-vertical>
      </b-col>
    </b-row>

    <b-card>
      <b-row>
        <b-col md="4" class="pb-1">
          <b-button
            v-if="hasPermission('case-management', 'create')"
            variant="outline-primary"
            :to="{ name: 'organization-case-add' }"
          >
            <feather-icon
              icon="PlusIcon"
              class="mr-50"
            />
            {{ $t('message.Add_Case') }}
          </b-button>
        </b-col>

        <b-col md="4">
          <b-button
            variant="outline-secondary"
            :disabled="clearFiltersDisabled"
            @click="clearFilters"
          >
            <feather-icon
              icon="TrashIcon"
              class="mr-50"
            />
            {{ $t('clear_filters') }}
          </b-button>
        </b-col>
  
        <b-col md="4">
          <b-form-group>
            <b-form-input
              v-model="searchQuery"
              :placeholder="$t('message.search')"
              type="search"
              class="d-inline-block"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col>
          <filters 
            :workerFilterShow="true"
            :pendingTasksFilterShow="true"
            @changeFilter="updateFilter"
          />
        </b-col>
  
        <b-col cols="12">
          <b-table
            ref="refCaseTable"
            :items="cases"
            hover
            responsive
            :fields="tableColumns"
            primary-key="id"
            :sort-by.sync="sortBy"
            show-empty=""
            :empty-text="isLoading ? $t('message.loading') : $t('message.no_records')"
            :sort-desc.sync="isSortDirDesc"
          >
            <template #cell(stage)="data">
              <span>
                {{ data.item.stageComputed.value }}
              </span>
            </template>

            <template #cell(state)="data">
              <span :class="`font-weight-bold ${data.item.state === 'completed' ? 'text-success' : data.item.state === 'delayed' ? 'text-danger' : 'text-primary'}`">
                {{ data.item.stateComputed.value }}
              </span>
              <span v-if="data.item.state === 'completed' && data.item.isCompletedOverdue" class="font-weight-bold text-danger">
                ({{ $t('delayed') }})
              </span>
            </template>
  
            <template #cell(actions)="data">
              <div class="flex flex-column">
                <ViewButton
                  variant="primary"
                  @clicked="router.push({name: 'organization-case-view', params: { id: data.item._id }})"
                />
                <EditButton
                  v-if="hasPermission('case-management', 'edit')"
                  i18nHoverVariable="Execute"
                  @clicked="router.push({name: 'organization-case-edit', params: { id: data.item._id }})"
                />
                <PrintButton v-if="hasPermission('case-management', 'export')" @clicked="downloadPDF(data.item._id)" />
                <DeleteButton v-if="hasPermission('case-management', 'delete')" v-b-modal="`modal ${data.item._id}`"/>
                <b-modal
                  :id="`modal ${data.item._id}`"
                  centered
                  no-close-on-backdrop
                  modal-class="modal-danger"
                  ok-variant="danger"
                  cancel-variant="outline-secondary"
                  :title="$t('message.confirm_action')"
                  :ok-title="$t('message.delete')"
                  :cancel-title="$t('message.cancel')"
                  @ok="deleteCase(data.item)"
                >
                  {{ $t('message.confirm_delete_improvement') }}
                </b-modal>
                <SlashButton
                  v-if="hasPermission('case-management', 'delete') && (data.item.stage !== 'end' || data.item.finishData)"
                  :variant="data.item.finishData ? 'success' : 'warning'"
                  :i18nHoverVariable="data.item.finishData ? 'message.see_data' : 'message.finish'"
                  v-b-modal="`finish-modal ${data.item._id}`"
                />
                <b-modal
                  :id="`finish-modal ${data.item._id}`"
                  centered
                  :no-close-on-backdrop="!data.item.finishData"
                  :modal-class="`modal-${data.item.finishData ? 'success' : 'warning'}`"
                  ok-variant="warning"
                  cancel-variant="outline-secondary"
                  :title="data.item.finishData ? $t('message.finish_data') : $t('message.confirm_action')"
                  :ok-title="$t('message.finish')"
                  :cancel-title="$t('message.cancel')"
                  :ok-disabled="!finishJustification"
                  :hide-footer="!!data.item.finishData"
                  @hidden="resetFinishModal"
                  @ok="finishCase(data.item, finishJustification, evidenceFiles)"
                >
                  <div v-if="!data.item.finishData" class="mb-2">
                    {{ $t('message.confirm_finish_case') }}
                  </div>
                  <div>
                    <!-- Justification -->
                    <b-form-group :label="$t('label.Justification')" :label-for="$t('label.Justification')">
                      <div v-if="data.item.finishData">
                        {{ data.item.finishData.justification }}
                      </div>
                      <div v-else>
                        <b-form-input v-model="finishJustification" />
                        <small v-if="!finishJustification" class="text-danger">
                          {{ $t('message.justification_required') }}
                        </small>
                      </div>
                    </b-form-group>

                    <!-- Open/Upload Evidences -->
                    <b-form-group :label="$t('label.Evidence_s')" :label-for="$t('label.Evidence_s')">
                      <!-- Document Repository -->
                      <document-repository
                        :documents="evidenceFiles"
                        :keys="data.item.finishData ? data.item.finishData.evidenceKeys : []"
                        :read-only="data.item.finishData && data.item.finishData.evidenceKeys && data.item.finishData.evidenceKeys.length > 0"
                        type="evidence"
                        @add="handleAddEvidences"
                        @delete="handleDeleteEvidences"
                      />
                    </b-form-group>
                  </div>
                </b-modal>
              </div>
            </template>
          </b-table>
        </b-col>
  
        <b-col
          cols="12"
          class="d-flex justify-content-between flex-wrap"
        >
          <div class="mb-1">
            <b-form-group class="mb-0">
              <label class="d-inline-block text-sm-left mr-50">{{ $t('message.per_page') }}</label>
              <b-form-select
                id="perPageSelect"
                v-model="perPage"
                size="sm"
                :options="[5, 10, 15, 20, 25, 30, 40, 50, 100]"
                class="per-page-selector"
              />
            </b-form-group>
          </div>
  
          <div class="mb-1">
            <span class="text-muted">{{ $tc('message.paginationText', 0, { from: `${((currentPage * perPage) - perPage) + (cases.length ? 1 : 0)}`, to: `${cases.length + ((currentPage * perPage) - perPage)}`, total: `${totalCases}` }) }}</span>
          </div>
  
          <b-pagination
            v-model="currentPage"
            :total-rows="totalCases"
            :per-page="perPage"
            first-number=""
            last-number=""
            prev-class="prev-item"
            next-class="next-item"
          />
        </b-col>
      </b-row>
    </b-card>
  </div>
</template>

<script>
import { BBadge, BPagination, BTable, BModal, VBModal } from 'bootstrap-vue'
import vSelect from 'vue-select'
import { onMounted } from '@vue/composition-api'
import { useRouter } from '@core/utils/utils'
import ViewButton from '@/views/components/Shared/Buttons/ViewButton.vue'
import EditButton from '@/views/components/Shared/Buttons/EditButton.vue'
import DeleteButton from '@/views/components/Shared/Buttons/DeleteButton.vue'
import SlashButton from '@/views/components/Shared/Buttons/SlashButton.vue'
import PrintButton from '@/views/components/Shared/Buttons/PrintButton.vue'
import useCaseList from './useCaseList'
import Filters from "@/views/organization/Filters.vue";
import StatisticCardVertical from '@/views/habit/StatisticCardVertical.vue'
import DonutChartState from './charts/ApexDonutChartState.vue'
import DonutChartStage from './charts/ApexDonutChartStage.vue'
import BarChartWorker from './charts/ChartjsHorizontalBarChartWorker.vue'
import store from '@/store'
import { hasPermission } from '@/composables/usePermission'
import { inputFileTypes } from '@/constants'
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import DocumentRepository from '@/views/habit/DocumentRepository'

export default {
  components: {
    BTable,
    BBadge,
    BPagination,
    ViewButton,
    EditButton,
    DeleteButton,
    SlashButton,
    PrintButton,
    vSelect,
    Filters,
    BModal,
    StatisticCardVertical,
    DonutChartState,
    DonutChartStage,
    BarChartWorker,
    Loading,
    DocumentRepository,
  },
  directives: {
    'b-modal': VBModal,
  },
  data() {
    return {
      items: [],
    }
  },
  setup() {
    const { role } = store.state?.userStore?.userData
    const { route, router } = useRouter()
    const {
      fetchCases,
      tableColumns,
      perPage,
      currentPage,
      totalCases,
      searchQuery,
      sortBy,
      isSortDirDesc,
      deleteCase,
      refCaseTable,
      cases,
      isLoading,
      caseStats,
      updateFilter,
      filterByState,
      filterByStage,
      activeState,
      statesData,
      stagesData,
      workersData,
      clearFilters,
      clearFiltersDisabled,
      finishCase,
      finishJustification,
      resetFinishModal,
      evidenceFiles,
      downloadPDF,
      isDownloading,
      handleAddEvidences,
      handleDeleteEvidences,
    } = useCaseList()

    onMounted(async () => {
      if (route.value.query.state) {
        activeState.value = route.value.query.state
      }

      fetchCases()
    })

    return {
      router,
      fetchCases,
      tableColumns,
      perPage,
      currentPage,
      totalCases,
      searchQuery,
      sortBy,
      isSortDirDesc,
      deleteCase,
      refCaseTable,
      cases,
      updateFilter,
      isLoading,
      caseStats,
      filterByState,
      filterByStage,
      activeState,
      statesData,
      stagesData,
      workersData,
      clearFilters,
      clearFiltersDisabled,
      role,
      hasPermission,
      finishCase,
      finishJustification,
      resetFinishModal,
      inputFileTypes,
      evidenceFiles,
      downloadPDF,
      isDownloading,
      handleAddEvidences,
      handleDeleteEvidences,
    }
  },
}
</script>

<style lang="scss" scoped>
.form-group {
  label {
    font-size: 0.85rem;
    font-weight: 400;
  }
}

.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
